<template>
    <ul class="pl-5.5">
        <li v-for="item in sortedCheckboxes" :key="String(item.id)" class="relative">
            <label
                class="flex items-start py-1 text-body"
                :class="{
                    'pr-9': item.children.length > 0,
                    // Remove opacity-50 temporarily since the disabled state from backend is not reliable
                    // 'opacity-50': item.isDisabled && !item.isSelected,
                }"
            >
                <input
                    ref="checkbox"
                    v-model="item.isSelected"
                    :data-name="item.name"
                    type="checkbox"
                    :value="item.id"
                    :indeterminate.prop="indeterminateMap[String(item.id)]"
                    class="Custom-checkbox"
                    @change="emit('updated', item)"
                />
                <span class="inline-flex items-start gap-1">
                    <!-- Hide block icon temporarily, since the disabled state from backend is not reliable -->
                    <!-- <span v-if="item.isDisabled" class="block mt-0.5">
                        <BanIcon class="size-4" />
                    </span> -->

                    {{
                        props.expressionBase
                            ? __untypedExpression(
                                  props.expressionBase +
                                      item.name.toLowerCase().replaceAll(' ', '-')
                              )
                            : item.name
                    }}
                </span>
            </label>

            <button
                v-if="item.children.length > 0"
                :aria-label="openStates[String(item.id)] ? __('collapse') : __('expand')"
                class="absolute p-0.5 -right-2 top-1 cursor-pointer rounded-sm hover:bg-gray-lightest"
                @click.prevent="openStates[String(item.id)] = !openStates[String(item.id)]"
            >
                <ChevronDownIcon
                    class="size-5"
                    :class="{
                        'rotate-180': openStates[String(item.id)],
                        'opacity-0': props.filter && hasMatchingDescendant(item, props.filter),
                    }"
                />
            </button>

            <NestedCheckboxListItems
                v-show="
                    (item.children.length > 0 && openStates[String(item.id)]) ||
                    (props.filter && hasMatchingDescendant(item, props.filter))
                "
                :checkboxes="item.children"
                :open-states="openStates"
                :filter="props.filter"
                :expression-base="props.expressionBase"
                @updated="emit('updated', $event)"
            />
        </li>
    </ul>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { EchoFilterCheckbox } from '@/js/services/api/filter/types';
    import { hasSelectedDescendant } from '@/js/services/api/filter/utils';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { ChevronDownIcon } from '@lucide/vue';

    const openStates = defineModel<Record<string, boolean>>('openStates', { required: true });

    const props = defineProps<{
        checkboxes: EchoFilterCheckbox[];
        filter?: string;
        expressionBase?: string;
    }>();

    const emit = defineEmits<{
        (e: 'updated', item: EchoFilterCheckbox): void;
    }>();

    const normalize = (string: string) =>
        string
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

    const matchesFilter = (name: string, filter?: string): boolean => {
        if (!filter) return true;

        const n = normalize(name);
        const words = normalize(filter).split(/\s+/);

        return words.every((word) => n.includes(word));
    };

    const hasMatchingDescendant = (item: EchoFilterCheckbox, filter?: string): boolean => {
        if (!filter) return true;
        if (item.isHidden) return false;

        if (matchesFilter(item.name, filter)) return true;

        if (item.children?.length > 0) {
            return item.children.some((child) => hasMatchingDescendant(child, filter));
        }

        return false;
    };

    const indeterminateMap = computed(() => {
        const map: Record<string, boolean> = {};
        for (const item of props.checkboxes) {
            map[String(item.id)] = !item.isSelected && hasSelectedDescendant(item);
        }
        return map;
    });

    const filteredCheckboxes = computed(() => {
        const visible = props.checkboxes.filter((item) => !item.isHidden);
        if (!props.filter) return visible;

        return visible.filter((item) => hasMatchingDescendant(item, props.filter));
    });

    const sortedCheckboxes = computed(() =>
        [...filteredCheckboxes.value].sort((a, b) => {
            // if (a.isDisabled !== b.isDisabled) return a.isDisabled ? 1 : -1;
            // For now, we sort all items alphabetically, since the disabled state from backend is not reliable.
            return a.name.localeCompare(b.name);
        })
    );
</script>
